import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { FeatureFlagsGateway } from 'src/app/domain/models/feature-flags/gateway/feature-flags-gateway';
import { IFeatureFlags } from 'src/app/domain/interfaces/feature-flags/interface-feature-flags';
import { environment } from 'src/environments/environment';

interface IFeatureFlagsResponse {
  data: IFeatureFlags;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService extends FeatureFlagsGateway {
  private readonly flags$ = new BehaviorSubject<IFeatureFlags>({});
  public readonly flagsChanged$ = this.flags$.asObservable();
  // A cold page load (direct URL entry, browser refresh) can run a flag-gated component's
  // ngOnInit before this constructor's own refresh() below resolves. Without this gate,
  // flagsChanged$'s synchronous initial {} would make isEnabled() emit false immediately, before
  // the real flag value ever arrives. shareReplay(1) lets every subscriber -- including one that
  // subscribes well after this service is created -- wait for that first result exactly once, no
  // matter how many times isEnabled()/getAllFlags() are called afterwards. (Ported from
  // member-portal-web's feature-flags.service.ts, which hit this race in its own sandbox run.)
  private readonly ready$ = this.refresh().pipe(shareReplay(1));

  constructor(private http: HttpClient) {
    super();
    this.ready$.subscribe();
  }

  public refresh(): Observable<IFeatureFlags> {
    // No IS_PUBLIC marker here (unlike member-portal-web): this repo's JwtInterceptor only adds
    // an Authorization header when a token already exists in storage and never refreshes it, so
    // there's no "unauthenticated visit forces a token refresh first" race to guard against.
    return this.http.get<IFeatureFlagsResponse>(`${environment.apiUrl}/feature-flags`).pipe(
      map((response) => response.data),
      tap((flags) => this.flags$.next(flags)),
      catchError(() => of(this.flags$.value)),
    );
  }

  public isEnabled(key: string): Observable<boolean> {
    return this.ready$.pipe(
      switchMap(() => this.flagsChanged$),
      map((flags) => Boolean(flags[key])),
    );
  }

  public getAllFlags(): Observable<IFeatureFlags> {
    return this.ready$.pipe(switchMap(() => this.flagsChanged$));
  }
}
